import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { ChevronDown, ChevronUp, Mail, Building2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

type Lead = {
  id: string;
  affiliate_id: string;
  name: string;
  email: string;
  organization: string | null;
  project_summary: string;
  budget_range: string | null;
  timeline: string | null;
  status: string;
  notes: string | null;
  created_at: string;
};

const STATUSES = ["new", "contacted", "closed"] as const;
const statusVariant: Record<string, "default" | "secondary" | "outline"> = {
  new: "default",
  contacted: "secondary",
  closed: "outline",
};

const LeadsTab = ({ affiliateIds }: { affiliateIds: string[] }) => {
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [noteDrafts, setNoteDrafts] = useState<Record<string, string>>({});

  const { data: leads, isLoading } = useQuery({
    enabled: affiliateIds.length > 0,
    queryKey: ["affiliate-leads", affiliateIds],
    queryFn: async (): Promise<Lead[]> => {
      const { data, error } = await supabase
        .from("affiliate_leads")
        .select("*")
        .in("affiliate_id", affiliateIds)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as Lead[];
    },
  });

  const stats = useMemo(() => {
    const all = leads ?? [];
    const thirtyAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
    return {
      total: all.length,
      new: all.filter((l) => l.status === "new").length,
      contacted: all.filter((l) => l.status === "contacted").length,
      closed: all.filter((l) => l.status === "closed").length,
      last30: all.filter((l) => new Date(l.created_at).getTime() >= thirtyAgo).length,
    };
  }, [leads]);

  const filtered = useMemo(() => {
    if (!leads) return [];
    if (statusFilter === "all") return leads;
    return leads.filter((l) => l.status === statusFilter);
  }, [leads, statusFilter]);

  const updateStatus = async (id: string, status: string) => {
    const { error } = await supabase
      .from("affiliate_leads")
      .update({ status })
      .eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Status updated");
    qc.invalidateQueries({ queryKey: ["affiliate-leads"] });
  };

  const saveNotes = async (id: string) => {
    const notes = noteDrafts[id] ?? "";
    const { error } = await supabase
      .from("affiliate_leads")
      .update({ notes })
      .eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Notes saved");
    qc.invalidateQueries({ queryKey: ["affiliate-leads"] });
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: "Total", value: stats.total },
          { label: "New", value: stats.new },
          { label: "Contacted", value: stats.contacted },
          { label: "Closed", value: stats.closed },
          { label: "Last 30 days", value: stats.last30 },
        ].map((s) => (
          <div key={s.label} className="rounded-lg border border-border bg-card p-4">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">{s.label}</div>
            <div className="text-2xl font-display font-bold mt-1">{s.value}</div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-sm text-muted-foreground">Filter:</span>
        {["all", ...STATUSES].map((s) => (
          <Button
            key={s}
            size="sm"
            variant={statusFilter === s ? "default" : "outline"}
            onClick={() => setStatusFilter(s)}
            className="capitalize"
          >
            {s}
          </Button>
        ))}
      </div>

      {isLoading ? (
        <div className="text-center text-muted-foreground py-12">Loading leads…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-border rounded-xl bg-card/50">
          <p className="text-muted-foreground">
            No leads yet. They'll appear here when someone requests a quote from your profile.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((lead) => {
            const open = expanded === lead.id;
            return (
              <div key={lead.id} className="rounded-lg border border-border bg-card overflow-hidden">
                <button
                  className="w-full flex items-start justify-between gap-3 p-4 text-left hover:bg-muted/40 transition-colors"
                  onClick={() => setExpanded(open ? null : lead.id)}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold">{lead.name}</span>
                      <Badge variant={statusVariant[lead.status] ?? "secondary"} className="capitalize">
                        {lead.status}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(lead.created_at), { addSuffix: true })}
                      </span>
                    </div>
                    <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                      <span className="inline-flex items-center gap-1">
                        <Mail className="h-3 w-3" /> {lead.email}
                      </span>
                      {lead.organization ? (
                        <span className="inline-flex items-center gap-1">
                          <Building2 className="h-3 w-3" /> {lead.organization}
                        </span>
                      ) : null}
                    </div>
                    <p className="mt-2 text-sm text-foreground/80 line-clamp-2">
                      {lead.project_summary}
                    </p>
                  </div>
                  {open ? (
                    <ChevronUp className="h-4 w-4 text-muted-foreground mt-1" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-muted-foreground mt-1" />
                  )}
                </button>

                {open ? (
                  <div className="border-t border-border p-4 space-y-4 bg-background/40">
                    <div className="grid sm:grid-cols-2 gap-3 text-sm">
                      <div>
                        <div className="text-xs uppercase tracking-wider text-muted-foreground">Budget</div>
                        <div>{lead.budget_range || "—"}</div>
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-wider text-muted-foreground">Timeline</div>
                        <div>{lead.timeline || "—"}</div>
                      </div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                        Project summary
                      </div>
                      <p className="text-sm whitespace-pre-wrap">{lead.project_summary}</p>
                    </div>

                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-sm text-muted-foreground">Status:</span>
                      <Select
                        value={lead.status}
                        onValueChange={(v) => updateStatus(lead.id, v)}
                      >
                        <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {STATUSES.map((s) => (
                            <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button asChild variant="outline" size="sm">
                        <a href={`mailto:${lead.email}`}>Reply by email</a>
                      </Button>
                    </div>

                    <div>
                      <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                        Private notes
                      </div>
                      <Textarea
                        rows={3}
                        defaultValue={lead.notes ?? ""}
                        onChange={(e) =>
                          setNoteDrafts({ ...noteDrafts, [lead.id]: e.target.value })
                        }
                      />
                      <div className="flex justify-end mt-2">
                        <Button size="sm" onClick={() => saveNotes(lead.id)}>Save notes</Button>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LeadsTab;